import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react"

import { endOfWeek, format, startOfWeek } from "date-fns"
import { ChevronDown, ChevronRight } from "lucide-react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ukDayEnd, ukToday } from "@/constants/sheriffOptions"
import { useShifts } from "@/hooks/useShifts"
import { ukDayEndOf, ukDayStartOf } from "@/lib/ukCalendarDate"
import { exportHRStatsToCSV } from "@/utils/csvExportUtils"

import { HRViewSkeleton } from "./SkeletonLoaders"

export interface HRViewRef {
  exportToCSV: () => void
}

interface HRViewProps {
  date: Date
  scheduleType: "delivery" | "collection" | "sortation" | "internal"
  filters: {
    location: string
    employer: string
    type: string
    areas: string[]
  }
  searchTerm: string
  onCountChange?: (count: number) => void
}

interface PersonStats {
  person: string
  numberOfShifts: number
  latenessCount: number
  latenessPercent: number
  awolCount: number
  awolPercent: number
  scheduledHours: number
  clockedHours: number
  autoClockOutCount: number
  autoClockOutPercent: number
}

interface TeamStats {
  team: string
  people: PersonStats[]
  totalShifts: number
}

const HRView = forwardRef<HRViewRef, HRViewProps>(
  ({ date, scheduleType: _scheduleType, filters, searchTerm, onCountChange }, ref) => {
    const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>({})

    // Build function request for the week containing the selected date
    const { weekStart, weekEnd } = useMemo(() => {
      const start = ukDayStartOf(startOfWeek(date, { weekStartsOn: 1 })) // Monday
      const end = ukDayEndOf(endOfWeek(date, { weekStartsOn: 1 })) // Sunday

      const now = new Date()
      const today = ukDayEnd(ukToday(now))

      // Check if the selected date's week includes today
      const isCurrentWeek = now >= start && now <= end

      // If current week, limit to today; otherwise use full week
      const effectiveEnd = isCurrentWeek ? today : end

      return {
        weekStart: start.toISOString(),
        weekEnd: effectiveEnd.toISOString(),
      }
    }, [date])

    const { data: shifts = [], isLoading } = useShifts({
      startTime: weekStart,
      endTime: weekEnd,
      location: filters.location !== "all" ? filters.location : undefined,
      employer: filters.employer !== "all" ? filters.employer : undefined,
      type: filters.type !== "all" ? filters.type : undefined,
      areas: filters.areas.length > 0 ? filters.areas : undefined,
    })

    // Calculate statistics grouped by team and person
    const teamStats = useMemo(() => {
      const stats: Record<string, Record<string, PersonStats>> = {}

      shifts.forEach((shift) => {
        const team = shift.area || "Unknown"
        const person = shift.people?.person || "Unassigned"

        // Skip unassigned shifts
        if (person === "Unassigned") return

        // Initialize team if not exists
        if (!stats[team]) {
          stats[team] = {}
        }

        // Initialize person if not exists
        if (!stats[team][person]) {
          stats[team][person] = {
            person,
            numberOfShifts: 0,
            latenessCount: 0,
            latenessPercent: 0,
            awolCount: 0,
            awolPercent: 0,
            scheduledHours: 0,
            clockedHours: 0,
            autoClockOutCount: 0,
            autoClockOutPercent: 0,
          }
        }

        const personStats = stats[team][person]

        // Increment shift count
        personStats.numberOfShifts++

        // Calculate scheduled hours
        const startTime = new Date(shift.startTime)
        const endTime = new Date(shift.endTime)
        const scheduledHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)
        personStats.scheduledHours += scheduledHours

        // Calculate clocked hours
        if (shift.timesheet?.clockedInTime && shift.timesheet?.clockedOutTime) {
          const clockedIn = new Date(shift.timesheet.clockedInTime)
          const clockedOut = new Date(shift.timesheet.clockedOutTime)
          const clockedHours = (clockedOut.getTime() - clockedIn.getTime()) / (1000 * 60 * 60)
          personStats.clockedHours += clockedHours
        }

        // Check for lateness
        if (shift.minutesLate && shift.minutesLate > 0) {
          personStats.latenessCount++
        }

        // Check for AWOL
        if (shift.awol) {
          personStats.awolCount++
        }

        // Check for auto clock out
        if (shift.timesheet?.autoClockOut) {
          personStats.autoClockOutCount++
        }
      })

      // Calculate percentages
      Object.values(stats).forEach((teamPeople) => {
        Object.values(teamPeople).forEach((personStats) => {
          if (personStats.numberOfShifts > 0) {
            personStats.latenessPercent =
              (personStats.latenessCount / personStats.numberOfShifts) * 100
            personStats.awolPercent = (personStats.awolCount / personStats.numberOfShifts) * 100
            personStats.autoClockOutPercent =
              (personStats.autoClockOutCount / personStats.numberOfShifts) * 100
          }
        })
      })

      return stats
    }, [shifts])

    // Convert to array format for rendering
    const teamStatsArray: TeamStats[] = useMemo(() => {
      return Object.entries(teamStats).map(([team, people]) => ({
        team,
        people: Object.values(people).sort((a, b) => a.person.localeCompare(b.person)),
        totalShifts: Object.values(people).reduce((sum, p) => sum + p.numberOfShifts, 0),
      }))
    }, [teamStats])

    // Filter by search term
    const filteredTeamStats = useMemo(() => {
      if (!searchTerm) return teamStatsArray

      return teamStatsArray
        .map((team) => ({
          ...team,
          people: team.people.filter((person) =>
            person.person.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        }))
        .filter((team) => team.people.length > 0)
    }, [teamStatsArray, searchTerm])

    // Calculate week range for display (matching the actual data range)
    // Must be before early returns to maintain hook order
    const { displayStartDate, displayEndDate } = useMemo(() => {
      const start = startOfWeek(date, { weekStartsOn: 1 })
      const end = endOfWeek(date, { weekStartsOn: 1 })

      const now = new Date()
      const today = new Date(now)

      // Check if the selected date's week includes today
      const isCurrentWeek = now >= start && now <= end

      // If current week, show up to today; otherwise show full week
      const effectiveEnd = isCurrentWeek ? today : end

      return {
        displayStartDate: start,
        displayEndDate: effectiveEnd,
      }
    }, [date])

    // Update count for parent
    React.useEffect(() => {
      const totalPeople = filteredTeamStats.reduce((sum, team) => sum + team.people.length, 0)
      onCountChange?.(totalPeople)
    }, [filteredTeamStats, onCountChange])

    const toggleTeam = (team: string) => {
      setExpandedTeams((prev) => ({
        ...prev,
        [team]: !prev[team],
      }))
    }

    const handleExportCSV = useCallback(() => {
      const filename = `hr_weekly_${format(displayStartDate, "yyyy-MM-dd")}_${format(displayEndDate, "yyyy-MM-dd")}.csv`
      exportHRStatsToCSV(filteredTeamStats, filename)
    }, [filteredTeamStats, displayStartDate, displayEndDate])

    useImperativeHandle(
      ref,
      () => ({
        exportToCSV: handleExportCSV,
      }),
      [handleExportCSV],
    )

    if (isLoading) {
      return <HRViewSkeleton />
    }

    if (filteredTeamStats.length === 0) {
      return (
        <div className="text-center py-16 text-palette-jungleGreen500">
          <p className="text-lg">No HR data found for this date</p>
        </div>
      )
    }

    const totalResults = filteredTeamStats.reduce((sum, team) => sum + team.people.length, 0)

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-palette-jungleGreen000 px-4 py-3 rounded-lg border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-palette-jungleGreen900">
              HR Schedule View ({totalResults})
            </h2>
            <span className="text-sm text-palette-jungleGreen600">
              {format(displayStartDate, "MMM d")} - {format(displayEndDate, "MMM d, yyyy")}
            </span>
          </div>
        </div>

        {/* Teams */}
        <div className="space-y-4">
          {filteredTeamStats.map((teamData) => {
            const isExpanded = expandedTeams[teamData.team] !== false // Default to expanded
            return (
              <div key={teamData.team} className="border rounded-lg overflow-hidden">
                {/* Team Header */}
                <div
                  className="bg-palette-jungleGreen000 px-4 py-3 border-b cursor-pointer hover:bg-palette-jungleGreen100 transition-colors"
                  onClick={() => toggleTeam(teamData.team)}
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-palette-jungleGreen600" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-palette-jungleGreen600" />
                    )}
                    <h3 className="font-semibold text-palette-jungleGreen900">
                      {teamData.team} ({teamData.people.length})
                    </h3>
                  </div>
                </div>

                {/* Team Table */}
                {isExpanded && (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">Person</TableHead>
                          <TableHead className="w-[120px] text-right">Number of Blocks</TableHead>
                          <TableHead className="w-[120px] text-right">Lateness Count</TableHead>
                          <TableHead className="w-[100px] text-right">Lateness %</TableHead>
                          <TableHead className="w-[100px] text-right">AWOL Count</TableHead>
                          <TableHead className="w-[100px] text-right">AWOL %</TableHead>
                          <TableHead className="w-[130px] text-right">Scheduled Hours</TableHead>
                          <TableHead className="w-[130px] text-right">Clocked Hours</TableHead>
                          <TableHead className="w-[130px] text-right">Auto Clock-out</TableHead>
                          <TableHead className="w-[130px] text-right">Auto Clock-out %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teamData.people.map((person) => (
                          <TableRow key={person.person}>
                            <TableCell className="font-medium">{person.person}</TableCell>
                            <TableCell className="text-right">{person.numberOfShifts}</TableCell>
                            <TableCell className="text-right">{person.latenessCount}</TableCell>
                            <TableCell className="text-right">
                              {person.latenessPercent.toFixed(1)}%
                            </TableCell>
                            <TableCell className="text-right">{person.awolCount}</TableCell>
                            <TableCell className="text-right">
                              {person.awolPercent.toFixed(1)}%
                            </TableCell>
                            <TableCell className="text-right">
                              {person.scheduledHours.toFixed(1)}
                            </TableCell>
                            <TableCell className="text-right">
                              {person.clockedHours.toFixed(1)}
                            </TableCell>
                            <TableCell className="text-right">{person.autoClockOutCount}</TableCell>
                            <TableCell className="text-right">
                              {person.autoClockOutPercent.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  },
)

export default HRView
